<template>
    <TprmModal
        open
        allow-close
        close-on-backdrop
        title="Product updates"
        size="lg"
        additional-class-name="ProductUpdatesArchiveModal"
        @close="close"
    >
        <div class="ProductUpdatesArchive">
            <div v-if="isLoading" class="ProductUpdatesArchive__empty">
                Loading product updates...
            </div>
            <div v-else-if="isError" class="ProductUpdatesArchive__empty ProductUpdatesArchive__empty--error">
                <p>We could not load product updates.</p>
                <NewButton size="sm" variant="outline" color="secondary" @click="reload">
                    Retry
                </NewButton>
            </div>
            <div v-else-if="productUpdates.length === 0" class="ProductUpdatesArchive__empty">
                No product updates yet.
            </div>
            <ol v-else class="ProductUpdatesArchive__list">
                <li
                    v-for="productUpdate in productUpdates"
                    :key="productUpdate.id"
                    class="ProductUpdatesArchive__item"
                >
                    <div class="ProductUpdatesArchive__itemHeader">
                        <h3 class="ProductUpdatesArchive__itemTitle">
                            {{ productUpdate.title }}
                        </h3>
                        <span
                            v-if="!productUpdate.dismissed"
                            class="ProductUpdatesArchive__badge"
                        >
                            New
                        </span>
                    </div>
                    <p class="ProductUpdatesArchive__description">
                        {{ productUpdate.description }}
                    </p>
                    <div class="ProductUpdatesArchive__meta">
                        <span>{{ formatPublishedAt(productUpdate.publishedAt) }}</span>
                        <button
                            type="button"
                            class="ProductUpdatesArchive__link"
                            @click="openKb(productUpdate.kbUrl)"
                        >
                            Learn more
                        </button>
                    </div>
                </li>
            </ol>
        </div>
    </TprmModal>
</template>

<style lang="scss">
.ProductUpdatesArchive {
    &__empty {
        display: grid;
        justify-items: center;
        gap: 12px;
        padding: 28px 0;
        color: var(--eidTextSecondary, #5a6672);
        text-align: center;
    }

    &__empty--error {
        color: var(--eidError, #bf3b3b);
    }

    &__list {
        display: grid;
        gap: 14px;
        margin: 0;
        padding: 0;
        list-style: none;
        max-height: 64vh;
        overflow-y: auto;
    }

    &__item {
        display: grid;
        gap: 8px;
        padding: 14px;
        border: 1px solid #dde4ea;
        border-radius: 8px;
        background: #fff;
    }

    &__itemHeader {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
    }

    &__itemTitle {
        margin: 0;
        color: #25384c;
        font-size: 16px;
        line-height: 1.25;
        font-weight: 700;
    }

    &__description {
        margin: 0;
        color: #435365;
        font-size: 14px;
        line-height: 1.45;
    }

    &__badge {
        flex: 0 0 auto;
        padding: 3px 8px;
        border-radius: 999px;
        background: #e7f8f3;
        color: #14765d;
        font-size: 12px;
        line-height: 1.3;
        font-weight: 700;
    }

    &__meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        color: #697789;
        font-size: 12px;
    }

    &__link {
        padding: 0;
        border: none;
        background: transparent;
        color: var(--eidSecureBlue, #235f9c);
        font: inherit;
        font-weight: 700;
        cursor: pointer;
    }
}
</style>

<script lang="ts">
    import { Component, Vue } from '@evidentid/vue-property-decorator';

    import { ProductUpdate } from '@evidentid/tprm-portal-lib/models/product-updates';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { useProductUpdatesStore } from '@/modules/product-updates/store';

    @Component({
        components: { NewButton, TprmModal },
    })
    export default class ProductUpdatesArchive extends Vue {
        private get productUpdatesStore() {
            return useProductUpdatesStore();
        }

        private get productUpdates(): ProductUpdate[] {
            return this.productUpdatesStore.archiveProductUpdates;
        }

        private get isLoading(): boolean {
            return this.productUpdatesStore.archiveLoadStatus === OperationStatus.loading;
        }

        private get isError(): boolean {
            return this.productUpdatesStore.archiveLoadStatus === OperationStatus.error;
        }

        private reload(): void {
            this.productUpdatesStore.loadProductUpdatesArchive({ force: true });
        }

        private close(): void {
            this.productUpdatesStore.closeProductUpdatesArchive();
        }

        private openKb(kbUrl: string): void {
            window.open(kbUrl, '_blank', 'noopener,noreferrer');
        }

        private formatPublishedAt(publishedAt: string | null): string {
            if (!publishedAt) {
                return 'Not published';
            }
            return new Date(publishedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
    }
</script>
